import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-effect-dark border-b border-orange-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="text-3xl font-bold text-white cursor-pointer hover:scale-105 transition-transform" 
              onClick={() => window.location.href = '/'}
            >
              <span className="text-orange-400 font-black text-3xl">Mega</span>videosee
            </div>
            <Button
              onClick={() => window.location.href = '/'}
              variant="outline"
              className="text-gray-300 border-gray-600 hover:text-orange-400 hover:border-orange-500"
            >
              ← Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white flex items-center">
              <span className="mr-3 text-4xl">🔒</span>
              Privacy Policy
            </CardTitle>
            <p className="text-gray-400 text-lg">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </CardHeader>
          
          <CardContent className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-medium text-orange-400 mb-2">1.1 Personal Information</h3>
                  <p>
                    We may collect personal information that you voluntarily provide to us when you:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                    <li>Contact us through our contact forms</li>
                    <li>Report content through our reporting system</li>
                    <li>Request help or support</li>
                    <li>Subscribe to our newsletters or updates</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-medium text-orange-400 mb-2">1.2 Automatically Collected Information</h3>
                  <p>
                    When you visit our website, we automatically collect certain information about your device, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                    <li>IP address and location information</li>
                    <li>Browser type and version</li>
                    <li>Device information and operating system</li>
                    <li>Pages visited and time spent on our website</li>
                    <li>Referring websites and search terms used to find our site</li>
                  </ul>
                </div>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect for the following purposes:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>To provide, maintain, and improve our website and services</li>
                <li>To respond to your comments, questions, and provide customer service</li>
                <li>To analyze usage patterns and optimize user experience</li>
                <li>To detect, prevent, and address technical issues and security threats</li>
                <li>To comply with legal obligations and protect our rights</li>
                <li>To send you updates, marketing communications (with your consent)</li>
              </ul>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Information Sharing and Disclosure</h2>
              <div className="space-y-4">
                <p>
                  We do not sell, trade, or rent your personal information to third parties. We may share your 
                  information in the following limited circumstances:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website</li>
                  <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong>Safety and Security:</strong> To protect the rights, property, or safety of our users or others</li>
                  <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                </ul>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Cookies and Tracking Technologies</h2>
              <div className="space-y-4">
                <p>
                  We use cookies and similar tracking technologies to enhance your browsing experience:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
                  <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
                  <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
                </ul>
                <p className="text-sm">
                  You can control cookies through your browser settings, but disabling certain cookies may affect website functionality.
                </p>
              </div>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. However, no method 
                of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Your Rights and Choices</h2>
              <p className="mb-4">Depending on your jurisdiction, you may have the following rights:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li><strong>Access:</strong> Request access to your personal information</li>
                <li><strong>Correction:</strong> Request correction of inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your data to another service</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Restriction:</strong> Request restriction of processing</li>
              </ul>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Age Verification and Restrictions</h2>
              <p>
                Our website contains adult content and is strictly intended for users 18 years of age or older. 
                We do not knowingly collect personal information from individuals under 18. If we become aware 
                that we have collected information from someone under 18, we will take steps to delete such information.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. International Data Transfers</h2>
              <p>
                Your information may be transferred to and processed in countries other than your country of 
                residence. We ensure that such transfers comply with applicable data protection laws and 
                implement appropriate safeguards to protect your information.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by 
                posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage 
                you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <Separator className="bg-gray-700" />

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Contact Us</h2>
              <div className="space-y-4">
                <p>
                  If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
                </p>
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-600">
                  <p className="text-orange-400 font-medium">Email:</p>
                  <p>privacy@megavideosee.com</p>
                  
                  <p className="text-orange-400 font-medium mt-3">Address:</p>
                  <p>
                    Megavideosee Legal Department<br />
                    Privacy Policy Inquiries<br />
                    [Your Business Address]
                  </p>
                </div>
              </div>
            </section>

            <div className="mt-12 text-center">
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-orange-600 hover:bg-orange-700 px-8 py-3 text-lg"
              >
                Return to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;